import type { PackageInfoChangeType, PackageInfoSnapshotType } from '../graphql/types/OrderDetailType';

/**
 * 内部轨迹「客户修改包裹信息」对比卡片的行数据（纯函数，不依赖 i18n 与格式化函数，便于单测）。
 * 展示口径：修改前/修改后都按公制字段换算成 lb/in，由组件调用 formatWeightKgToLb / formatDimensions。
 */
export interface PackageMetricValues {
    weightKg: number | null;
    lengthCm: number | null;
    widthCm: number | null;
    heightCm: number | null;
    volumetricWeightKg: number | null;
}

export type UnitSystem = 'metric' | 'imperial';

export interface PackageUnitSwitch {
    /** 仅当修改前后单位制都已知且不同时为 true */
    switched: boolean;
    from: UnitSystem | null;
    to: UnitSystem | null;
}

export interface PackageInfoComparison {
    weightChanged: boolean;
    dimensionsChanged: boolean;
    before: PackageMetricValues;
    after: PackageMetricValues;
    unitSwitch: PackageUnitSwitch;
}

/** 降级为 LogScan 时灰框只列本次提交的组：未提交的组为 null */
export interface PackageSubmittedValues {
    weight: { weightKg: number | null } | null;
    dimensions: { lengthCm: number | null; widthCm: number | null; heightCm: number | null } | null;
}

const toMetricValues = (snapshot?: PackageInfoSnapshotType | null): PackageMetricValues => ({
    weightKg: snapshot?.weightKg ?? null,
    lengthCm: snapshot?.lengthCm ?? null,
    widthCm: snapshot?.widthCm ?? null,
    heightCm: snapshot?.heightCm ?? null,
    volumetricWeightKg: snapshot?.volumetricWeightKg ?? null,
});

const toUnitSystem = (useImperialUnit?: boolean | null): UnitSystem | null => {
    if (useImperialUnit == null) {
        return null;
    }
    return useImperialUnit ? 'imperial' : 'metric';
};

export function buildPackageInfoComparison(change?: PackageInfoChangeType | null): PackageInfoComparison {
    const from = toUnitSystem(change?.before?.useImperialUnit);
    const to = toUnitSystem(change?.after?.useImperialUnit);
    return {
        weightChanged: change?.weightChanged === true,
        dimensionsChanged: change?.dimensionsChanged === true,
        before: toMetricValues(change?.before),
        after: toMetricValues(change?.after),
        unitSwitch: { switched: from != null && to != null && from !== to, from, to },
    };
}

export function buildSubmittedValues(change?: PackageInfoChangeType | null): PackageSubmittedValues {
    const after = toMetricValues(change?.after);
    return {
        weight: change?.weightChanged === true ? { weightKg: after.weightKg } : null,
        dimensions: change?.dimensionsChanged === true
            ? { lengthCm: after.lengthCm, widthCm: after.widthCm, heightCm: after.heightCm }
            : null,
    };
}
